import { useState } from 'react';

import type { WalletRecipientView } from '@/api/address-book-bff/types';
import {
  classifyRecipient,
  getNormalizedSuiAddress,
  type RecipientKind,
} from '@/shared/modules/wallet/send/recipient';

const INVALID_RECIPIENT = 'Choose a contact or enter a valid Sui address.';
const SPONSORED_GAS = 'Gas Fees are sponsored by Gamma Prime';

const recipientHelperText = (invalidRecipient: boolean, kind: RecipientKind): string => {
  if (invalidRecipient) {
    return INVALID_RECIPIENT;
  }

  return kind === 'empty' ? '' : SPONSORED_GAS;
};

const matchesEnteredAddress = (input: string, recipient: WalletRecipientView): boolean => {
  const enteredAddress = getNormalizedSuiAddress(input);
  return !!enteredAddress && enteredAddress === getNormalizedSuiAddress(recipient.wallet.address);
};

const describeRecipient = (
  recipientInput: string,
  selectedRecipient: WalletRecipientView | null,
  recipientTouched: boolean,
) => {
  const recipientAddress =
    getNormalizedSuiAddress(selectedRecipient?.wallet.address ?? '') ??
    getNormalizedSuiAddress(recipientInput) ??
    undefined;
  const kind = classifyRecipient(recipientInput);
  const invalidRecipient =
    recipientTouched && recipientInput.trim().length > 0 && recipientAddress === undefined;
  return {
    recipientInput,
    selectedRecipient,
    recipientTouched,
    kind,
    recipientAddress,
    invalidRecipient,
    recipientHelper: recipientHelperText(invalidRecipient, kind),
  };
};

export const useSendRecipient = () => {
  const [recipientInput, setInput] = useState('');
  const [selectedRecipient, setSelected] = useState<WalletRecipientView | null>(null);
  const [recipientTouched, setTouched] = useState(false);

  const replaceRecipient = (input: string, selected: WalletRecipientView | null) => {
    setInput(input);
    setSelected(selected);
    setTouched(false);
  };

  return {
    ...describeRecipient(recipientInput, selectedRecipient, recipientTouched),
    setRecipientInput: (input: string) => {
      if (input !== recipientInput) {
        setSelected(null);
      }

      setInput(input);
    },
    resolveRecipientAddress: (address: string) => {
      const normalizedAddress = getNormalizedSuiAddress(address);

      if (normalizedAddress) {
        replaceRecipient(normalizedAddress, null);
      }
    },
    selectRecipient: (recipient: WalletRecipientView) =>
      replaceRecipient(recipient.wallet.address, recipient),
    enrichRecipientIdentity: (recipient: WalletRecipientView) => {
      if (matchesEnteredAddress(recipientInput, recipient)) {
        setSelected(recipient);
      }
    },
    editRecipient: () => setSelected(null),
    clearRecipient: () => replaceRecipient('', null),
    handleRecipientBlur: () => setTouched(true),
  };
};
